/**
 * CrmLandingDesigns — CRM Landing Page Designs.
 * Gallery of block-based homepage templates (Classic + 10 designs). Admin can
 * activate a design, and edit any design's blocks (reorder via drag / up-down,
 * hide, delete, add from library, and edit text/image/font/colour). Each design
 * stores its own saved config, so switching never loses customisations.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Reorder, useDragControls } from 'framer-motion';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { Label } from '../../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../components/ui/dialog';
import { toast } from 'sonner';
import {
  GripVertical, ArrowUp, ArrowDown, Trash2, Eye, EyeOff, Plus, Save, RotateCcw,
  CheckCircle2, ChevronDown, ChevronRight, ExternalLink, Monitor, Smartphone,
  Image as ImageIcon, Palette, ArrowLeft, Layers, Sparkles, Copy,
} from 'lucide-react';
import { LandingRenderer } from '../../components/landing/LandingRenderer';
import { DesignThumb } from '../../components/landing/designThumbs';
import { BLOCK_META, BLOCK_SCHEMAS, THEME_FIELDS } from '../../components/landing/landingMeta';
import { ImagePicker } from '../../components/cms/ImagePicker';
import { FONT_FAMILIES } from '../../lib/fonts';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const ax = { withCredentials: true };
const uid = () => (window.crypto?.randomUUID ? window.crypto.randomUUID() : `b_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`);

// ── field-level editors ───────────────────────────────────────────────────────
const LINK_TYPES = [
  { value: 'none', label: 'None' },
  { value: 'experience', label: 'Experience Page' },
  { value: 'package', label: 'Package Page' },
  { value: 'custom', label: 'Custom URL' },
];

const LinkField = ({ value, onChange, linkOptions, testid }) => {
  const v = value && typeof value === 'object' ? value : { type: 'none', target: '', text: '' };
  const set = (patch) => onChange({ ...v, ...patch });
  const experiences = linkOptions?.experiences || [];
  const packages = linkOptions?.packages || [];
  const selCls = 'w-full h-9 rounded-md border border-[#E8E5DF] px-2 text-sm bg-white';
  return (
    <div className="space-y-2 rounded-lg border border-dashed border-[#D8D4CC] p-2 bg-white">
      <div>
        <Label className="text-[11px] text-[#6B6B7A]">Link type</Label>
        <select value={v.type || 'none'} onChange={(e) => set({ type: e.target.value, target: '' })} className={selCls} data-testid={testid ? `${testid}-type` : undefined}>
          {LINK_TYPES.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </div>
      {v.type === 'experience' && (
        <div>
          <Label className="text-[11px] text-[#6B6B7A]">Experience page</Label>
          <select value={v.target || ''} onChange={(e) => set({ target: e.target.value })} className={selCls} data-testid={testid ? `${testid}-experience` : undefined}>
            <option value="">Select an experience…</option>
            {experiences.map((x) => <option key={x.slug} value={x.slug}>{x.title} ({x.slug})</option>)}
          </select>
        </div>
      )}
      {v.type === 'package' && (
        <div>
          <Label className="text-[11px] text-[#6B6B7A]">Package</Label>
          <select value={v.target || ''} onChange={(e) => set({ target: e.target.value })} className={selCls} data-testid={testid ? `${testid}-package` : undefined}>
            <option value="">Select a package…</option>
            {packages.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
          </select>
        </div>
      )}
      {v.type === 'custom' && (
        <div>
          <Label className="text-[11px] text-[#6B6B7A]">Custom URL</Label>
          <Input value={v.target || ''} onChange={(e) => set({ target: e.target.value })} placeholder="https://…" data-testid={testid ? `${testid}-custom` : undefined} />
        </div>
      )}
      {v.type && v.type !== 'none' && (
        <div>
          <Label className="text-[11px] text-[#6B6B7A]">Link text</Label>
          <Input value={v.text || ''} onChange={(e) => set({ text: e.target.value })} placeholder="Learn more" data-testid={testid ? `${testid}-text` : undefined} />
        </div>
      )}
    </div>
  );
};

const ScalarField = ({ field, value, onChange, testid, linkOptions }) => {
  if (field.type === 'textarea') return <Textarea value={value ?? ''} onChange={(e) => onChange(e.target.value)} rows={3} data-testid={testid} />;
  if (field.type === 'image') return <ImagePicker value={value} onChange={onChange} testid={testid} hint="Upload" />;
  if (field.type === 'link') return <LinkField value={value} onChange={onChange} linkOptions={linkOptions} testid={testid} />;
  if (field.type === 'number') return <Input type="number" value={value ?? ''} onChange={(e) => onChange(Number(e.target.value))} data-testid={testid} />;
  if (field.type === 'color') return (
    <div className="flex items-center gap-2">
      <input type="color" value={value || '#000000'} onChange={(e) => onChange(e.target.value)} className="h-9 w-12 rounded border border-[#E8E5DF] cursor-pointer" data-testid={testid ? `${testid}-picker` : undefined} />
      <Input value={value || ''} onChange={(e) => onChange(e.target.value)} className="font-mono" data-testid={testid} />
    </div>
  );
  if (field.type === 'font') return (
    <select value={value || ''} onChange={(e) => onChange(e.target.value)} className="w-full h-9 rounded-md border border-[#E8E5DF] px-2 text-sm bg-white" data-testid={testid}>
      {FONT_FAMILIES.map((f) => <option key={f.label} value={f.value}>{f.label}</option>)}
    </select>
  );
  if (field.type === 'select') return (
    <select value={value ?? ''} onChange={(e) => { const raw = e.target.value; const num = Number(raw); onChange(field.options.every((o) => typeof o === 'number') ? num : raw); }} className="w-full h-9 rounded-md border border-[#E8E5DF] px-2 text-sm bg-white capitalize" data-testid={testid}>
      {field.options.map((o) => <option key={o} value={o}>{String(o)}</option>)}
    </select>
  );
  return <Input value={value ?? ''} onChange={(e) => onChange(e.target.value)} data-testid={testid} />;
};

const ListField = ({ field, value, onChange, testidPrefix, linkOptions }) => {
  const arr = Array.isArray(value) ? value : [];
  const setItem = (i, patch) => onChange(arr.map((it, idx) => (idx === i ? { ...it, ...patch } : it)));
  const move = (i, d) => { const j = i + d; if (j < 0 || j >= arr.length) return; const c = arr.slice(); [c[i], c[j]] = [c[j], c[i]]; onChange(c); };
  const del = (i) => onChange(arr.filter((_, idx) => idx !== i));
  const blank = (f) => (f.type === 'number' ? 5 : f.type === 'link' ? { type: 'none', target: '', text: 'Learn more' } : '');
  const add = () => onChange([...arr, Object.fromEntries(field.itemFields.map((f) => [f.key, blank(f)]))]);
  return (
    <div className="space-y-2">
      {arr.map((it, i) => (
        <div key={i} className="rounded-lg border border-[#E8E5DF] p-2.5 bg-[#FAF8F4]" data-testid={`${testidPrefix}-item-${i}`}>
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-[11px] font-semibold text-[#6B6B7A]">Item {i + 1}</span>
            <div className="flex items-center gap-0.5">
              <button type="button" onClick={() => move(i, -1)} disabled={i === 0} className="p-1 disabled:opacity-30" data-testid={`${testidPrefix}-up-${i}`}><ArrowUp className="w-3.5 h-3.5" /></button>
              <button type="button" onClick={() => move(i, 1)} disabled={i === arr.length - 1} className="p-1 disabled:opacity-30" data-testid={`${testidPrefix}-down-${i}`}><ArrowDown className="w-3.5 h-3.5" /></button>
              <button type="button" onClick={() => del(i)} className="p-1 text-[#FF3B6B]" data-testid={`${testidPrefix}-del-${i}`}><Trash2 className="w-3.5 h-3.5" /></button>
            </div>
          </div>
          <div className="space-y-2">
            {field.itemFields.map((f) => (
              <div key={f.key}>
                <Label className="text-[11px] text-[#6B6B7A]">{f.label}</Label>
                <ScalarField field={f} value={it[f.key]} onChange={(v) => setItem(i, { [f.key]: v })} testid={`${testidPrefix}-${i}-${f.key}`} linkOptions={linkOptions} />
              </div>
            ))}
          </div>
        </div>
      ))}
      <Button type="button" variant="outline" size="sm" onClick={add} className="rounded-full" data-testid={`${testidPrefix}-add`}><Plus className="w-3.5 h-3.5 mr-1" />Add item</Button>
    </div>
  );
};

const BlockFields = ({ block, onChange, linkOptions }) => {
  const schema = BLOCK_SCHEMAS[block.type] || [];
  const setProp = (key, val) => onChange({ ...block.props, [key]: val });
  return (
    <div className="space-y-3">
      {block.type === 'packages' && <p className="text-[11px] text-[#6B6B7A] italic">Package cards & prices are pulled live from your Packages settings and link to /book.</p>}
      {schema.map((f) => (
        <div key={f.key}>
          <Label className="text-xs font-medium">{f.label}</Label>
          {f.type === 'list'
            ? <ListField field={f} value={block.props[f.key]} onChange={(v) => setProp(f.key, v)} testidPrefix={`bf-${block.id}-${f.key}`} linkOptions={linkOptions} />
            : <ScalarField field={f} value={block.props[f.key]} onChange={(v) => setProp(f.key, v)} testid={`bf-${block.id}-${f.key}`} linkOptions={linkOptions} />}
        </div>
      ))}
    </div>
  );
};

// ── one draggable block row ─────────────────────────────────────────────────
const BlockRow = ({ block, index, total, expanded, onExpand, onMove, onToggleHide, onDelete, onChange, linkOptions }) => {
  const controls = useDragControls();
  const meta = BLOCK_META[block.type] || { label: block.type };
  return (
    <Reorder.Item value={block} dragListener={false} dragControls={controls}
      className="rounded-xl border border-[#E8E5DF] bg-white" data-testid={`block-row-${block.id}`}>
      <div className="flex items-center gap-1.5 p-2.5">
        <button type="button" onPointerDown={(e) => controls.start(e)} className="cursor-grab active:cursor-grabbing text-[#B7B3AC] touch-none" title="Drag to reorder" data-testid={`block-drag-${block.id}`}><GripVertical className="w-4 h-4" /></button>
        <button type="button" onClick={onExpand} className="flex-1 flex items-center gap-2 text-left min-w-0" data-testid={`block-toggle-${block.id}`}>
          {expanded ? <ChevronDown className="w-4 h-4 shrink-0" /> : <ChevronRight className="w-4 h-4 shrink-0" />}
          <span className={`text-sm font-semibold truncate ${block.hidden ? 'text-[#B7B3AC] line-through' : 'text-[#1E1E24]'}`}>{meta.label}</span>
          {block.hidden && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-[#F0EEE9] text-[#6B6B7A] shrink-0">hidden</span>}
        </button>
        <div className="flex items-center gap-0.5 shrink-0">
          <button type="button" onClick={() => onMove(index, -1)} disabled={index === 0} className="p-1.5 disabled:opacity-30" data-testid={`block-up-${block.id}`}><ArrowUp className="w-4 h-4" /></button>
          <button type="button" onClick={() => onMove(index, 1)} disabled={index === total - 1} className="p-1.5 disabled:opacity-30" data-testid={`block-down-${block.id}`}><ArrowDown className="w-4 h-4" /></button>
          <button type="button" onClick={onToggleHide} className="p-1.5" title={block.hidden ? 'Show' : 'Hide'} data-testid={`block-hide-${block.id}`}>{block.hidden ? <EyeOff className="w-4 h-4 text-[#B7B3AC]" /> : <Eye className="w-4 h-4 text-[#06D6A0]" />}</button>
          <button type="button" onClick={onDelete} className="p-1.5 text-[#FF3B6B]" title="Delete block" data-testid={`block-delete-${block.id}`}><Trash2 className="w-4 h-4" /></button>
        </div>
      </div>
      {expanded && <div className="border-t border-[#E8E5DF] p-3"><BlockFields block={block} onChange={(props) => onChange({ ...block, props })} linkOptions={linkOptions} /></div>}
    </Reorder.Item>
  );
};

// ── main page ─────────────────────────────────────────────────────────────────
export const CrmLandingDesigns = () => {
  const [list, setList] = useState([]);
  const [activeId, setActiveId] = useState('classic');
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // {id, name}
  const [blocks, setBlocks] = useState([]);
  const [theme, setTheme] = useState({});
  const [library, setLibrary] = useState([]);
  const [expandedId, setExpandedId] = useState(null);
  const [showTheme, setShowTheme] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [previewMobile, setPreviewMobile] = useState(false);
  const [copyPrompt, setCopyPrompt] = useState(null);
  const [copyBusy, setCopyBusy] = useState(false);
  const [linkOptions, setLinkOptions] = useState({ experiences: [], packages: [] });

  useEffect(() => {
    let ok = true;
    Promise.all([
      fetch(`${API_URL}/api/cms/content`).then((r) => (r.ok ? r.json() : {})).catch(() => ({})),
      fetch(`${API_URL}/api/packages`).then((r) => (r.ok ? r.json() : [])).catch(() => []),
    ]).then(([cms, pkgs]) => {
      if (!ok) return;
      const exItems = (cms?.experiences?.items || []).map((x) => ({
        slug: x.id || (x.href || '').split('/').pop(),
        title: x.title || x.id,
      })).filter((x) => x.slug);
      const pkgArr = (Array.isArray(pkgs) ? pkgs : (pkgs.packages || [])).map((p) => ({ id: p.id, name: p.name }));
      setLinkOptions({ experiences: exItems, packages: pkgArr });
    });
    return () => { ok = false; };
  }, []);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/admin/landing/designs`, ax);
      setList(res.data?.designs || []);
      setActiveId(res.data?.active_design_id || 'classic');
    } catch { toast.error('Could not load designs'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { fetchList(); }, [fetchList]);

  const openEditor = async (id) => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/landing/designs/${id}`, ax);
      const d = res.data?.design;
      setEditing({ id: d.id, name: d.name, customized: d.customized });
      setBlocks(d.blocks || []);
      setTheme(d.theme || {});
      setLibrary(res.data?.block_library || []);
      setExpandedId(null); setShowTheme(false); setShowAdd(false); setDirty(false);
      window.scrollTo({ top: 0 });
    } catch { toast.error('Could not open design'); }
  };

  const activate = async (id) => {
    try {
      await axios.post(`${API_URL}/api/admin/landing/activate`, { design_id: id }, ax);
      setActiveId(id);
      toast.success(id === 'classic' ? 'Classic homepage is now live' : 'Design is now live on your homepage');
      fetchList();
    } catch { toast.error('Could not activate'); }
  };

  // Copy-content prompt: when making a fresh design live, offer to carry over
  // the current homepage's content; also available as an editor action.
  const requestMakeLive = (d) => {
    if (d.is_classic || d.customized) { activate(d.id); return; }
    setCopyPrompt({ targetId: d.id, then: 'activate' });
  };
  const runCopy = async (source) => {
    const target = copyPrompt?.targetId;
    if (!target) return;
    setCopyBusy(true);
    try {
      const res = await axios.post(`${API_URL}/api/admin/landing/designs/${target}/copy-content`, { source }, ax);
      const d = res.data?.design;
      if (editing?.id === target && d) { setBlocks(d.blocks || []); setTheme(d.theme || {}); setDirty(false); }
      toast.success('Content copied into this design');
      if (copyPrompt?.then === 'activate') await activate(target);
      fetchList();
    } catch (err) { toast.error(err.response?.data?.detail || 'Copy failed'); }
    finally { setCopyBusy(false); setCopyPrompt(null); }
  };
  const skipCopy = async () => {
    if (copyPrompt?.then === 'activate' && copyPrompt?.targetId) await activate(copyPrompt.targetId);
    setCopyPrompt(null);
  };

  const save = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/admin/landing/designs/${editing.id}`, { blocks, theme }, ax);
      setDirty(false);
      toast.success('Design saved');
      fetchList();
    } catch (err) { toast.error(err.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  const resetToTemplate = async () => {
    if (!editing) return;
    if (!window.confirm('Reset this design to its original template? Your customisations for this design will be lost.')) return;
    try {
      const res = await axios.post(`${API_URL}/api/admin/landing/designs/${editing.id}/reset`, {}, ax);
      const d = res.data?.design;
      setBlocks(d.blocks || []); setTheme(d.theme || {}); setDirty(false);
      toast.success('Reset to template');
      fetchList();
    } catch { toast.error('Reset failed'); }
  };

  const markDirty = () => setDirty(true);
  const move = (i, d) => { const j = i + d; if (j < 0 || j >= blocks.length) return; const c = blocks.slice(); [c[i], c[j]] = [c[j], c[i]]; setBlocks(c); markDirty(); };
  const toggleHide = (i) => { setBlocks(blocks.map((b, idx) => (idx === i ? { ...b, hidden: !b.hidden } : b))); markDirty(); };
  const del = (i) => { setBlocks(blocks.filter((_, idx) => idx !== i)); markDirty(); };
  const updateBlock = (i, nb) => { setBlocks(blocks.map((b, idx) => (idx === i ? nb : b))); markDirty(); };
  const addBlock = (type) => {
    const lib = library.find((l) => l.type === type);
    const props = JSON.parse(JSON.stringify(lib?.default_props || {}));
    setBlocks([...blocks, { id: uid(), type, hidden: false, props }]);
    setShowAdd(false); setDirty(true);
    toast.success(`${BLOCK_META[type]?.label || type} added`);
  };
  const onReorder = (newOrder) => { setBlocks(newOrder); markDirty(); };
  const setThemeField = (k, v) => { setTheme({ ...theme, [k]: v }); markDirty(); };

  const previewDesign = useMemo(() => ({ id: editing?.id, name: editing?.name, theme, blocks }), [editing, theme, blocks]);

  const [copySource, setCopySource] = useState('classic');
  const targetName = copyPrompt ? (list.find((x) => x.id === copyPrompt.targetId)?.name || 'this design') : '';
  const copyDialog = (
    <Dialog open={!!copyPrompt} onOpenChange={(o) => { if (!o) setCopyPrompt(null); }}>
      <DialogContent className="bg-white max-w-md" data-testid="copy-content-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Fredoka, sans-serif' }}><Copy className="w-5 h-5 text-[#FF3B6B]" />Bring your content across?</DialogTitle>
          <DialogDescription className="text-xs text-[#6B6B7A]">
            Copy your existing homepage content into <b>{targetName}</b> — matching text, images and links fill the new design's blocks. You can still edit everything after.
          </DialogDescription>
        </DialogHeader>
        <div>
          <Label className="text-xs font-medium">Copy content from</Label>
          <select value={copySource} onChange={(e) => setCopySource(e.target.value)} className="w-full h-9 rounded-md border border-[#E8E5DF] px-2 text-sm bg-white mt-1" data-testid="copy-source-select">
            <option value="classic">Current homepage (Classic)</option>
            {list.filter((x) => !x.is_classic && x.id !== copyPrompt?.targetId).map((x) => (
              <option key={x.id} value={x.id}>{x.name}</option>
            ))}
          </select>
        </div>
        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="outline" type="button" onClick={skipCopy} disabled={copyBusy} data-testid="copy-start-fresh">Start fresh</Button>
          <Button type="button" onClick={() => runCopy(copySource)} disabled={copyBusy} className="bg-[#FF3B6B] hover:bg-[#e8305c] text-white" data-testid="copy-existing-content">{copyBusy ? 'Copying…' : 'Copy existing content'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );

  // ── Gallery view ──────────────────────────────────────────────────────────
  if (!editing) {
    return (
      <div className="p-4 sm:p-6 max-w-7xl mx-auto" data-testid="landing-designs-page">
        {copyDialog}
        <div className="flex items-start justify-between gap-4 flex-wrap mb-1">
          <div>
            <h1 className="text-2xl font-extrabold flex items-center gap-2" style={{ fontFamily: 'Fredoka, sans-serif' }}><Layers className="w-6 h-6 text-[#FF3B6B]" />Landing Page Designs</h1>
            <p className="text-sm text-[#6B6B7A] mt-1">Pick a homepage design, then click Edit to customise its blocks. Your current site stays on <b>Classic</b> until you activate another.</p>
          </div>
          <a href="/" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#FF3B6B] hover:underline" data-testid="view-live-site"><ExternalLink className="w-4 h-4" />View live site</a>
        </div>
        {loading ? (
          <div className="py-20 text-center text-[#6B6B7A]">Loading designs…</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-6" data-testid="design-gallery">
            {list.map((d) => (
              <div key={d.id} className={`rounded-2xl border-2 bg-white overflow-hidden transition-all ${d.active ? 'border-[#FF3B6B] shadow-lg' : 'border-[#E8E5DF] hover:border-[#FFB3C6]'}`} data-testid={`design-card-${d.id}`}>
                <div className="relative">
                  {d.is_classic
                    ? <div className="aspect-[1/1] bg-gradient-to-br from-[#FFDEE9] via-[#FFF4D6] to-[#B5FFFC] flex items-center justify-center"><Sparkles className="w-10 h-10 text-[#FF3B6B]" /></div>
                    : <DesignThumb theme={d.theme} blocks={d.blocks_preview} />}
                  {d.active && <span className="absolute top-2 left-2 text-[11px] font-bold px-2 py-1 rounded-full bg-[#FF3B6B] text-white shadow" data-testid={`active-badge-${d.id}`}>● LIVE</span>}
                  {d.customized && !d.is_classic && <span className="absolute top-2 right-2 text-[11px] font-semibold px-2 py-1 rounded-full bg-white/90 text-[#06D6A0] shadow">customised</span>}
                </div>
                <div className="p-4">
                  <h3 className="font-bold text-[#1E1E24]" style={{ fontFamily: 'Fredoka, sans-serif' }}>{d.name}</h3>
                  <p className="text-xs text-[#6B6B7A] mt-0.5 min-h-[32px]">{d.tagline}</p>
                  <div className="flex items-center gap-2 mt-3">
                    {!d.is_classic && <Button size="sm" variant="outline" className="rounded-full flex-1" onClick={() => openEditor(d.id)} data-testid={`edit-design-${d.id}`}>Edit</Button>}
                    {d.active
                      ? <Button size="sm" className="rounded-full flex-1 bg-[#06D6A0] hover:bg-[#05b98a] text-white" disabled data-testid={`active-btn-${d.id}`}><CheckCircle2 className="w-4 h-4 mr-1" />Live</Button>
                      : <Button size="sm" className="rounded-full flex-1 bg-[#FF3B6B] hover:bg-[#e8305c] text-white" onClick={() => requestMakeLive(d)} data-testid={`activate-design-${d.id}`}>Make Live</Button>}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  // ── Editor view ─────────────────────────────────────────────────────────────
  return (
    <div className="p-3 sm:p-5 max-w-[1500px] mx-auto" data-testid="design-editor">
      {copyDialog}
      <div className="flex items-center gap-3 flex-wrap mb-4 sticky top-0 z-10 bg-[#FAF8F4]/95 backdrop-blur py-2 -mx-3 px-3 sm:-mx-5 sm:px-5">
        <Button variant="ghost" size="sm" onClick={() => (dirty && !window.confirm('Discard unsaved changes?') ? null : setEditing(null))} data-testid="editor-back"><ArrowLeft className="w-4 h-4 mr-1" />Gallery</Button>
        <div className="flex-1 min-w-0">
          <h2 className="text-lg font-extrabold truncate" style={{ fontFamily: 'Fredoka, sans-serif' }}>{editing.name}{dirty && <span className="ml-2 text-xs font-normal text-[#FF7A2E]">● unsaved</span>}</h2>
        </div>
        <Button variant="outline" size="sm" className="rounded-full" onClick={resetToTemplate} data-testid="editor-reset"><RotateCcw className="w-4 h-4 mr-1" />Reset</Button>
        <Button variant="outline" size="sm" className="rounded-full" onClick={() => setCopyPrompt({ targetId: editing.id, then: null })} data-testid="editor-copy-content"><Copy className="w-4 h-4 mr-1" />Copy content</Button>
        {activeId !== editing.id && <Button variant="outline" size="sm" className="rounded-full" onClick={() => activate(editing.id)} data-testid="editor-activate">Make Live</Button>}
        <Button size="sm" className="rounded-full bg-[#FF3B6B] hover:bg-[#e8305c] text-white" onClick={save} disabled={saving || !dirty} data-testid="editor-save"><Save className="w-4 h-4 mr-1" />{saving ? 'Saving…' : 'Save'}</Button>
      </div>

      <div className="grid lg:grid-cols-[380px_1fr] gap-4">
        {/* Left: block list + theme */}
        <div className="space-y-3">
          <div className="rounded-2xl border border-[#E8E5DF] bg-white">
            <button type="button" onClick={() => setShowTheme((s) => !s)} className="w-full flex items-center justify-between p-3.5" data-testid="theme-toggle">
              <span className="font-bold flex items-center gap-2 text-[#1E1E24]"><Palette className="w-4 h-4 text-[#FF3B6B]" />Theme &amp; Fonts</span>
              {showTheme ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
            </button>
            {showTheme && (
              <div className="border-t border-[#E8E5DF] p-3 grid grid-cols-2 gap-2.5" data-testid="theme-panel">
                {THEME_FIELDS.map((f) => (
                  <div key={f.key} className={f.type === 'font' || f.type === 'select' ? 'col-span-2' : ''}>
                    <Label className="text-[11px] text-[#6B6B7A]">{f.label}</Label>
                    <ScalarField field={f} value={theme[f.key]} onChange={(v) => setThemeField(f.key, v)} testid={`theme-${f.key}`} />
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="flex items-center justify-between px-1">
            <span className="text-xs font-semibold uppercase tracking-widest text-[#6B6B7A]">Blocks ({blocks.length})</span>
            <div className="relative">
              <Button size="sm" variant="outline" className="rounded-full" onClick={() => setShowAdd((s) => !s)} data-testid="add-block-btn"><Plus className="w-3.5 h-3.5 mr-1" />Add block</Button>
              {showAdd && (
                <div className="absolute right-0 mt-1 z-20 w-56 max-h-72 overflow-y-auto rounded-xl border border-[#E8E5DF] bg-white shadow-lg p-1" data-testid="add-block-menu">
                  {library.map((l) => (
                    <button key={l.type} type="button" onClick={() => addBlock(l.type)} className="w-full text-left px-3 py-2 text-sm rounded-lg hover:bg-[#FAF8F4]" data-testid={`add-block-${l.type}`}>{BLOCK_META[l.type]?.label || l.label}</button>
                  ))}
                </div>
              )}
            </div>
          </div>

          <Reorder.Group axis="y" values={blocks} onReorder={onReorder} className="space-y-2" data-testid="block-list">
            {blocks.map((b, i) => (
              <BlockRow key={b.id} block={b} index={i} total={blocks.length}
                expanded={expandedId === b.id}
                onExpand={() => setExpandedId(expandedId === b.id ? null : b.id)}
                onMove={move} onToggleHide={() => toggleHide(i)} onDelete={() => del(i)}
                onChange={(nb) => updateBlock(i, nb)} linkOptions={linkOptions} />
            ))}
          </Reorder.Group>
          {blocks.length === 0 && <p className="text-sm text-[#6B6B7A] text-center py-6">No blocks yet — add one above.</p>}
        </div>

        {/* Right: live preview */}
        <div className="rounded-2xl border border-[#E8E5DF] bg-white overflow-hidden">
          <div className="flex items-center justify-between px-4 py-2 border-b border-[#E8E5DF] bg-[#FAF8F4]">
            <span className="text-xs font-semibold text-[#6B6B7A]">Live preview</span>
            <div className="flex items-center gap-1">
              <button type="button" onClick={() => setPreviewMobile(false)} className={`p-1.5 rounded-md ${!previewMobile ? 'bg-white shadow text-[#FF3B6B]' : 'text-[#6B6B7A]'}`} data-testid="preview-desktop"><Monitor className="w-4 h-4" /></button>
              <button type="button" onClick={() => setPreviewMobile(true)} className={`p-1.5 rounded-md ${previewMobile ? 'bg-white shadow text-[#FF3B6B]' : 'text-[#6B6B7A]'}`} data-testid="preview-mobile"><Smartphone className="w-4 h-4" /></button>
            </div>
          </div>
          <div className="overflow-auto max-h-[75vh] bg-[#F0EEE9]" data-testid="preview-scroll">
            <div className={`mx-auto bg-white transition-all ${previewMobile ? 'w-[390px]' : 'w-full'}`}>
              <div style={previewMobile ? { zoom: 0.9 } : {}}>
                <LandingRenderer design={previewDesign} previewBlocks={blocks} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CrmLandingDesigns;
